import { makeAutoObservable, runInAction } from 'mobx'
import type { IDropdownItem } from 'shared/ui'
import { prepareSearchPhoneNumber } from 'shared/lib'
import { type Phone } from 'entities/Phone/model/Phone'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type {
  IMultiselectedItem,
  INumbersGroup,
} from 'pages/settings/pages/compliance/ui/MultiSelectNumbers/type'

type IParentStore = {
  searchByNumbersOnly?: boolean
  setLoading: (loading: boolean) => void
  setPreAssignedNumber?: (preAssignedNumber: IDropdownItem | null) => void
}

export class CommonNumbersStore {
  private _search = ''
  private _assignedListMap: Map<number, Phone> = new Map()
  private _unassignedListMap: Map<number, Phone> = new Map()
  private _assignedDropdownMap: Map<number, IDropdownItem> = new Map()
  private _unassignedDropdownMap: Map<number, IDropdownItem> = new Map()
  private _multiSelectedValuesMap: Map<number, IMultiselectedItem> = new Map()
  private _isInitialized = false

  constructor(private _parentStore: IParentStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = async ({
    assignedNumbers,
    unassignedNumbers,
    preAssignedNumber,
  }: {
    assignedNumbers?: Phone[]
    unassignedNumbers: Phone[]
    preAssignedNumber?: IDropdownItem | null
  }) => {
    if (this._isInitialized) return

    const { setLoading, setPreAssignedNumber } = this.parentStore

    runInAction(() => {
      setLoading(true)
    })
    await this._entities.numbersStore.fetchNumbers()

    this._isInitialized = true

    this.setAssignedNumbers(assignedNumbers)
    this.initUnassignedNumbers(unassignedNumbers)

    if (preAssignedNumber) {
      this.setMultipleItem(preAssignedNumber)
      setPreAssignedNumber && setPreAssignedNumber(null)
    }

    runInAction(() => {
      this.parentStore.setLoading(false)
    })
  }

  get parentStore() {
    return this._parentStore
  }

  get searchByNumbersOnly() {
    return this._parentStore.searchByNumbersOnly
  }

  get isInitialized() {
    return this._isInitialized
  }

  get assignedList() {
    return Array.from(this._assignedListMap.values())
  }

  get assignedDropdownNumbers() {
    return Array.from(this._assignedDropdownMap.values())
  }

  get unassignedList() {
    return Array.from(this._unassignedListMap.values())
  }

  get unassignedDropdownNumbers() {
    return Array.from(this._unassignedDropdownMap.values())
  }

  get search() {
    return this._search
  }

  get multiSelectedKeys() {
    return Array.from(this._multiSelectedValuesMap.keys())
  }

  get multiSelectedValues() {
    return Array.from(this._multiSelectedValuesMap.values())
  }

  get multiSelectedNumbers() {
    return this.multiSelectedValues.map((item) => item.data.number)
  }

  get isDeselectState() {
    return this.itemsCount === 0 && this.multiSelectedKeys.length > 0
  }

  get itemsCount() {
    return this.assignedDropdownNumbers.length + this.unassignedDropdownNumbers.length
  }

  get isStepDisabled() {
    return !this.multiSelectedKeys.length
  }

  get numbersGroups(): INumbersGroup[] {
    const items: INumbersGroup[] = []

    if (this.assignedDropdownNumbers.length) {
      items.push({
        key: 'assigned',
        title: '',
        items: this.getItems(this.assignedDropdownNumbers),
      })
    }

    if (this.unassignedDropdownNumbers.length) {
      items.push({
        key: 'unassigned',
        title: '',
        items: this.getItems(this.unassignedDropdownNumbers),
      })
    }

    if (this._search) {
      return items.filter((group) => group.items.length)
    }

    return items
  }

  getItems = (numbersList: IDropdownItem[]): IDropdownItem[] => {
    return numbersList
      .filter((item) => {
        const numberItem = item.data as Phone
        if (this._multiSelectedValuesMap.has(numberItem.id)) return
        const numberSearchTerm = prepareSearchPhoneNumber(this._search)

        const searchByName = numberItem.numberable?.name
          .toLowerCase()
          .includes(this._search.toLowerCase())
        const searchByNumbers = numberItem.number.includes(numberSearchTerm)

        return Boolean(searchByName || searchByNumbers)
      }, [])
      .sort((a, b) => {
        const numberA = a.data as Phone
        const numberB = b.data as Phone

        const nameA = numberA.numberable?.name?.toLowerCase() || ''
        const nameB = numberB.numberable?.name?.toLowerCase() || ''
        const formattedNumberA = numberA.formatted_number
        const formattedNumberB = numberB.formatted_number

        if (nameA === nameB) {
          return formattedNumberA.localeCompare(formattedNumberB)
        }

        return nameA.localeCompare(nameB)
      })
  }

  initUnassignedNumbers = (unassignedNumbers: Phone[]) => {
    unassignedNumbers.forEach((phone) => {
      this._unassignedListMap.set(phone.id, phone)
      this.setDropdownItem(phone, this._unassignedDropdownMap)
    })
  }

  setAssignedNumbers = (assignedNumbers?: Phone[]) => {
    if (!assignedNumbers) return

    assignedNumbers.forEach((item) => {
      this._assignedListMap.set(item.id, item)
      this.setDropdownItem(item, this._assignedDropdownMap)
    })
  }

  setDropdownItem = (number: Phone, list: Map<number, IDropdownItem>) => {
    const item = {
      id: String(number.id),
      label: number.formatted_number,
      data: number,
      labelRight: number.numberable?.name || 'Unassigned',
      iconLabel: number?.is_aircall ? 'aircall' : undefined,
      ariaLabel: 'MultiSelectDropdownNumber',
      labelRightVariant: 'body-md-regular',
    } as IDropdownItem

    list.set(number.id, item)
  }

  setMultipleItem = (item: IDropdownItem) => {
    const phone = item.data as Phone
    const numberId = Number(item.id)
    const name = phone?.numberable?.name || 'Unassigned'
    const label = phone.formatted_number
    const isAssignedNumber = !!phone?.numberable?.name

    this._multiSelectedValuesMap.set(numberId, {
      id: numberId,
      name,
      label,
      data: phone,
    } as IMultiselectedItem)

    this.filterMultiselectListNumbers(isAssignedNumber)
  }

  handleMultiSelect = () => {
    const list = [...this._assignedDropdownMap, ...this._unassignedDropdownMap]

    list.forEach(([, item]) => this.setMultipleItem(item))
  }

  setRemovedItemToList = (item: IMultiselectedItem) => {
    if (item.name === 'Unassigned') {
      this.setDropdownItem(item.data, this._unassignedDropdownMap)
    } else
      this.setDropdownItem(
        {
          ...item.data,
          id: Number(item.id),
        } as Phone,
        this._assignedDropdownMap
      )
  }

  handleMultiDeselect = () => {
    this.multiSelectedValues.forEach((item) => {
      this.setRemovedItemToList(item)
    })
    this._multiSelectedValuesMap.clear()
  }

  removeMultiSelectedItem = (item: IMultiselectedItem) => {
    this._multiSelectedValuesMap.delete(item.id)
    this.setRemovedItemToList(item)
  }

  filterMultiselectListNumbers = (isAssignedNumber: boolean) => {
    const list = isAssignedNumber ? this._assignedDropdownMap : this._unassignedDropdownMap
    const filteredMap = new Map(
      Array.from(list).filter(([key]) => !this.multiSelectedKeys.includes(key))
    )

    isAssignedNumber
      ? (this._assignedDropdownMap = filteredMap)
      : (this._unassignedDropdownMap = filteredMap)
  }

  setSearch = (value: string) => {
    this._search = this.searchByNumbersOnly ? value.replace(/\D/g, '') : value
  }

  reset = () => {
    this._isInitialized = false
    this._search = ''
    this._assignedListMap.clear()
    this._unassignedListMap.clear()
    this._assignedDropdownMap.clear()
    this._unassignedDropdownMap.clear()
    this._multiSelectedValuesMap.clear()
  }
}
